import React, {FunctionComponent, Suspense, useState} from 'react';

import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import {Redirect, Route, Switch} from "react-router-dom";
import Herd from "../pages/herd";
import AnimalDashboard from "./AnimalDashboard";
import {
    Box,
    CircularProgress,
    createTheme,
    PaletteColor,
    PaletteColorOptions,
    SxProps,
    Theme,
    ThemeProvider,
    Toolbar,
    useMediaQuery
} from "@mui/material";
import NoMatch from "./Routing/no-match";
import ApplicationBar from "./application-bar";
import NavigationMenu from "./navigationMenu";
import RolesRoute from "./Permissions/roles-route";
import {LocalizationProvider} from "@mui/x-date-pickers";
import PregnancyDetails from "./AnimalDashboard/Pregnancy/pregnancy-details";
import ActionsMenu from "./Common/actions-menu";
import FindRegistry from "./Registry/find-registry";
import CreateRegistry from "./Registry/create-registry";
import RegistryAdministrationDetails from "./Registry/registry-administration-details";
import AddRegistryUser from "./Registry/add-registry-user";
import {RegistryUserType} from "../services/registry-service";
import RouteService from "../services/route-service";
import JoinRegistry from "./Registry/join-registry";
import SelectRegistryForAnimalRequest from "./Registry/select-registry-for-animal-request";
import ReviewList from "./AnimalReview/review-list";
import AnimalReview from "./AnimalReview/animal-review";
import SimilarityComparison from "./AnimalReview/similarity-comparison";
import RegistrationsSearchPage from "./Registry/registrations-search-page";
import RegisteredAnimalDetails from "../pages/registered-animal-details";
import AnimalCreate from "../pages/animal-create";
import PregnancyCreate from "../pages/pregnancy-create";
import SignupBreeder from "../pages/signup-breeder";
import {CheckoutSuccessPage} from "../pages/checkout-success-page";
import TagEditor from "../pages/tag-editor";
import TreatmentEditor from "../pages/treatment-editor";
import {blue, orange, pink} from "@mui/material/colors";
import {AdapterLuxon} from "@mui/x-date-pickers/AdapterLuxon";
import AnimalAncestry from '../pages/ancestry-change';
import AdminAccountsLink from "../pages/admin-accounts-link";
import AdminAccountsLinkEditor from "../pages/admin-accounts-link-editor";
import GlobalSnackbar from "./Common/global-snackbar";
import {AppFooter} from "./Common/app-footer";
import {BREEDER, PANON_ADMIN, REGISTRY_WARDEN} from "./Permissions/Roles";
import AuthService from "../services/auth-service";
import Breeders from "../pages/breeders";
import BreederDetails from "../pages/breeder-details";
import {HerdPregnancies} from "../pages/herd-pregnancies";
import {Dashboard} from "./Home/dashboard";
import MatingCreate from '../pages/mating-create';
import AnimalPdf from "./AnimalDashboard/Pdf/AnimalPdf";
import PendingRegistrations from "./Registry/pending-registrations";
import HelpPage from "../pages/help-page";
import HelpEditPage from "../pages/help-edit-page";
import Farm from "../pages/farm";
import {RegistryListPage} from './Registry/registry-list-page';
import {RegistryAnimalListPage} from './Registry/registry-animal-list-page';
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import OwnershipTransfer from "./AnimalDashboard/Ownership/OwnershipTransfer";
import {ErrorBoundary} from "react-error-boundary";
import {ErrorBox} from "./Common/alerts";
import {DocumentsListPage} from "../pages/documents-list-page";
import {LoginPage} from "../pages/login-page";
import {AdminBreedersList, AdminBreedersPdf} from '../pages/admin-breeders-list';
import OwnershipTransfersPage from "../pages/ownership-transfers-page";
import {SubscriptionOverviewPage} from "../pages/subscription/subscription-overview-page";
import {AdminProductsPage} from "../pages/admin-products-page";
import {AdminFeatureFlagsPage} from "../pages/admin-feature-flags";


declare module '@mui/material/styles' {
    interface Palette {
        male: PaletteColor;
        female: PaletteColor;
    }

    interface PaletteOptions {
        male: PaletteColorOptions;
        female: PaletteColorOptions;
    }

    interface Theme {
        drawerWidth: number;
    }

    // allow configuration using `createTheme`
    interface ThemeOptions {
        drawerWidth?: number;
    }
}

const {palette} = createTheme();

const customTheme: Theme = createTheme({
    palette: {
        male: palette.augmentColor({color: blue}),
        female: palette.augmentColor({color: pink}),
        warning: palette.augmentColor({color: orange})
    },
    components: {
        MuiTextField: {
            defaultProps: {
                variant: "standard"
            }
        },
        MuiSelect: {
            defaultProps: {
                variant: "standard"
            }
        }
    },
    drawerWidth: 240
});

const LoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress/>
    </Box>
);

const styles: Record<string, SxProps> = {
    root: {
        display: 'flex'
    },
    drawer: {
        flexShrink: {sm: 0},
        width: "drawerWidth"
    },
    permanentNavigationMenu: {
        width: customTheme.drawerWidth,
        overflowX: "hidden",
        overflowY: "auto"}

} as const;

const contentStyles: SxProps<Theme> = {
    width: {
        xs: "100%",
        sm: `calc(100% - ${customTheme.drawerWidth}px)`
    },
    ml: {
        sm: `${customTheme.drawerWidth}px`
    },
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
} as const;

const RoutedPages = () => {
    if (!AuthService.isLoggedIn()) {
        return (
            <Switch>
                <Route path={RouteService.BREEDER_SIGNUP}><SignupBreeder/></Route>
                <Route path={RouteService.CHECKOUT_SUCCESS}><CheckoutSuccessPage/></Route>
                <Route path={RouteService.HELP}><HelpPage/></Route>
                <Route path={RouteService.HOME}><LoginPage/></Route>
                <Route path={RouteService.CATCH_ALL}><NoMatch/></Route>
            </Switch>
        );
    }

    return (
        <Switch>
            <RolesRoute roles={[BREEDER]} path={RouteService.BREEDER_SIGNUP}><Redirect to={RouteService.HOME}/></RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_LINK_ACCOUNT_WITH_BREEDER_EDITOR}>
                <AdminAccountsLinkEditor/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_LINK_ACCOUNT_WITH_BREEDER}>
                <AdminAccountsLink/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_BREEDER_LIST}>
                <AdminBreedersList/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_BREEDER_PDF}>
                <AdminBreedersPdf/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_PRODUCTS}>
                <AdminProductsPage/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADMIN_FEATURE_FLAGS}>
                <AdminFeatureFlagsPage/>
            </RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.HERD_GROUP}><Herd/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.HERD_PREGNANCIES_LIST}><HerdPregnancies/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.ANIMAL_CREATE}><AnimalCreate/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.REGISTERED_ANIMAL_DETAILS}><RegisteredAnimalDetails/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.ANIMAL_DETAILS_PDF}><AnimalPdf/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.ANIMAL_DETAILS}><AnimalDashboard/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.ANIMAL_CHANGE_ANCESTRY}><AnimalAncestry/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.ANIMAL_TRANSFER_OWNERSHIP}><OwnershipTransfer/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.ANIMAL_OWNERSHIP_TRANSFERS}><OwnershipTransfersPage/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.REGISTRY_ANIMAL_LIST}><RegistryAnimalListPage/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.ANIMAL_REGISTRY_APPLICATION_SELECT}><SelectRegistryForAnimalRequest/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.MATING_CREATE}><MatingCreate/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.PREGNANCY_CREATE}><PregnancyCreate/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.PREGNANCY_DETAILS}><PregnancyDetails/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.REGISTRY_JOIN}><JoinRegistry/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.REGISTRY_SEARCH}><FindRegistry/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.REGISTRY_ANIMAL_SEARCH}><RegistrationsSearchPage/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.REGISTRY_MY_REGISTRATIONS_LIST}><PendingRegistrations/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.TAGS_EDIT}><TagEditor/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.TREATMENT_EDIT}><TreatmentEditor/></RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]}
                        path={RouteService.REGISTRY_ADMINISTRATION_CREATE}><CreateRegistry/></RolesRoute>
            <RolesRoute roles={[BREEDER]}
                        path={RouteService.REGISTRY_ADMINISTRATION_DETAILS}><RegistryAdministrationDetails/></RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.REGISTRY_ADMINISTRATION_ADD_ADMIN}>
                <AddRegistryUser userType={RegistryUserType.ADMIN}/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.REGISTRY_ADMINISTRATION_ADD_WARDEN}>
                <AddRegistryUser userType={RegistryUserType.WARDEN}/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.REGISTRY_ADMINISTRATION_ADD_MEMBER}>
                <AddRegistryUser userType={RegistryUserType.MEMBER}/>
            </RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.ADD_HELP_ARTICLE}><HelpEditPage/></RolesRoute>
            <RolesRoute roles={[PANON_ADMIN]} path={RouteService.EDIT_HELP_ARTICLE}><HelpEditPage/></RolesRoute>
            <RolesRoute roles={[REGISTRY_WARDEN]}
                        path={RouteService.REGISTRY_ANIMAL_REVIEW_COMPARE_SIMILARITIES}><SimilarityComparison/></RolesRoute>
            <RolesRoute roles={[REGISTRY_WARDEN]}
                        path={RouteService.REGISTRY_ANIMAL_REVIEW}><AnimalReview/></RolesRoute>
            <RolesRoute roles={[REGISTRY_WARDEN]}
                        path={RouteService.REGISTRY_ANIMAL_REVIEW_LIST}><ReviewList/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.REGISTRY_LIST}><RegistryListPage/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.BREEDER_BASE_DATA}><Farm/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.BREEDERS_LIST}><Breeders/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.BREEDER_DETAILS}><BreederDetails/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.DOCUMENTS_LIST}><DocumentsListPage/></RolesRoute>
            <RolesRoute roles={[BREEDER]} path={RouteService.BREEDER_SUBSCRIPTION_OVERVIEW}><SubscriptionOverviewPage/></RolesRoute>
            <Route path={RouteService.CHECKOUT_SUCCESS}><CheckoutSuccessPage/></Route>
            <Route path={RouteService.HELP}><HelpPage/></Route>
            <RolesRoute roles={[BREEDER]} path={RouteService.HOME}><Dashboard/></RolesRoute>
            <Route path={RouteService.CATCH_ALL}><NoMatch/></Route>
        </Switch>
    );
};

const ThemedApp = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [overflowMenuAnchor, setOverflowMenuAnchor] = useState<HTMLElement | null>(null);

    const handleDrawerClose = () => setMobileOpen(false);
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const permanentDrawer = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

    return (
        <div translate="no">
            <CssBaseline/>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="de">
                <Box sx={styles.root}>
                    <ApplicationBar drawerWidth={customTheme.drawerWidth} onDrawerClick={handleDrawerToggle}
                                    onOverflowMenuClick={setOverflowMenuAnchor}/>
                    <ActionsMenu anchor={overflowMenuAnchor} onClose={() => setOverflowMenuAnchor(null)}/>
                    {permanentDrawer
                        ? <Drawer sx={styles.drawer} variant="permanent" open>
                            <NavigationMenu sx={styles.permanentNavigationMenu} onClick={handleDrawerClose}/>
                        </Drawer>
                        : <Drawer sx={styles.drawer} variant="temporary" anchor="left" open={mobileOpen}
                                  onClose={handleDrawerToggle} ModalProps={{keepMounted: true}}>
                            <NavigationMenu sx={{width: customTheme.drawerWidth}} onClick={handleDrawerClose}/>
                        </Drawer>}
                    <Box component="main" sx={contentStyles}>
                        <Toolbar/>
                        <Box sx={{flexGrow: 1}}>
                            <ErrorBoundary fallback={<ErrorBox>Fehler beim Anzeigen der Seite!</ErrorBox>}>
                                <Suspense fallback={<LoadingFallback/>}>
                                    <RoutedPages/>
                                </Suspense>
                            </ErrorBoundary>
                        </Box>
                        <AppFooter/>
                        <GlobalSnackbar/>
                    </Box>
                </Box>
            </LocalizationProvider>
        </div>
    );
};

const queryClient = new QueryClient();

const App: FunctionComponent = () => (
    <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={customTheme}>
            <ThemedApp/>
        </ThemeProvider>
    </QueryClientProvider>
);

export default App;
